import React, { useState } from 'react'
import {
  Box,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemText,
  Chip,
  Collapse,
  IconButton
} from '@mui/material'
import { useTranslation } from 'react-i18next'
import { motion } from 'framer-motion'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'

const booksData = require('./books.json')

const YearSection = ({ year, books, selectedLanguage, searchQuery, isExpanded, onToggle }) => {
  const { t } = useTranslation()
  // Filter books by language and search query
  const filteredBooks = books.filter(book => {
    const matchesLanguage = selectedLanguage === 'all' || book.language === selectedLanguage;
    const matchesSearch = !searchQuery || 
      book.title.toLowerCase().includes(searchQuery.toLowerCase()) || 
      book.author.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesLanguage && matchesSearch;
  });

  if (filteredBooks.length === 0) return null;

  return (
    <Box sx={{ mb: 2, width: '100%' }}>
      <Box 
        sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          mb: 2,
          borderBottom: '2px solid #007FFF',
          pb: 1,
          cursor: 'pointer'
        }}
        onClick={onToggle}
      >
        <Typography 
          variant="h5" 
          sx={{ 
            fontWeight: 'bold',
            background: 'linear-gradient(45deg, #007FFF 30%, #0059B2 90%)',
            backgroundClip: 'text',
            textFillColor: 'transparent',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
          }}
        >
          {year}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography 
            variant="body2" 
            sx={{ 
              color: 'text.secondary',
              fontWeight: 500
            }}
          >
            {filteredBooks.length}
          </Typography>
          <IconButton size="small" sx={{ color: '#007FFF' }}>
            {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </Box>
      </Box>

      <Collapse in={isExpanded} timeout="auto" unmountOnExit>
        <Paper 
          elevation={0} 
          sx={{ 
            borderRadius: 2,
            overflow: 'hidden',
            border: '1px solid',
            borderColor: 'divider'
          }}
        >
          <List>
            {filteredBooks.map((book, index) => (
              <motion.div
                key={`${book.title}-${index}`}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: index * 0.05 }}
              >
                <ListItem 
                  sx={{ 
                    borderBottom: index < filteredBooks.length - 1 ? '1px solid' : 'none',
                    borderColor: 'divider',
                    '&:hover': {
                      backgroundColor: 'rgba(0, 127, 255, 0.04)'
                    }
                  }}
                >
                  <ListItemText
                    primary={
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {book.title}
                      </Typography>
                    }
                    secondary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.2 }}>
                        <Typography variant="body2" color="text.secondary">
                          {book.author}
                        </Typography>
                        <Chip 
                          label={book.language === 'en' ? t('books:en') : t('books:kr')} 
                          size="small" 
                          sx={{ 
                            height: 20, 
                            fontSize: '0.7rem',
                            backgroundColor: book.language === 'en' ? 'rgba(0, 127, 255, 0.1)' : 'rgba(255, 99, 71, 0.1)',
                            color: book.language === 'en' ? '#007FFF' : '#FF6347'
                          }} 
                        />
                        {book.isCurrentlyReading && (
                          <Chip 
                            label={t('books:currentlyReading')} 
                            size="small" 
                            color="info" 
                            sx={{ height: 20, fontSize: '0.7rem' }} 
                          />
                        )}
                      </Box>
                    }
                  />
                </ListItem>
              </motion.div>
            ))}
          </List>
        </Paper>
      </Collapse>
    </Box>
  );
};

export default function DividerBooks({ selectedLanguage, searchQuery }) {
  const { t } = useTranslation();

  console.log("DividerBooks rendered with:", { selectedLanguage, searchQuery });
  console.log("Books data:", booksData);

  // Process the books data to move "current" books to 2025
  const processedBooksData = { ...booksData };
  
  // If there are books in the "current" section, add them to 2025 with isCurrentlyReading flag
  if (processedBooksData.current && processedBooksData.current.length > 0) {
    if (!processedBooksData["2025"]) {
      processedBooksData["2025"] = [];
    }
    
    // Add current books to 2025 with isCurrentlyReading flag
    const currentBooks = processedBooksData.current.map(book => ({
      ...book,
      isCurrentlyReading: true
    }));
    
    processedBooksData["2025"] = [...currentBooks, ...processedBooksData["2025"]];
    
    // Remove the "current" section
    delete processedBooksData.current;
  }

  // Get all years from the processed books data
  const years = Object.keys(processedBooksData).filter(year => 
    year !== 'pending' && processedBooksData[year].length > 0
  ).sort((a, b) => {
    // Sort years in descending order
    return b - a;
  });
  
  console.log("Processed years:", years);

  // State to track which year sections are expanded
  const [expandedYears, setExpandedYears] = useState(() => {
    // Initialize with all years collapsed by default
    const initial = {};
    years.forEach(year => {
      initial[year] = false;
      // initial[year] = year === "2025";
    });
    return initial;
  });
  
  console.log("Expanded years:", expandedYears);

  // Function to toggle a year section
  const toggleYear = (year) => {
    setExpandedYears(prev => ({
      ...prev,
      [year]: !prev[year]
    }));
  };

  return (
    <Box sx={{ width: '100%', maxWidth: '800px', mx: 'auto' }}>
      {years.map(year => (
        <YearSection 
          key={year} 
          year={year} 
          books={processedBooksData[year]} 
          selectedLanguage={selectedLanguage}
          searchQuery={searchQuery}
          isExpanded={expandedYears[year]}
          onToggle={() => toggleYear(year)}
        />
      ))}
            
      <Typography
        variant="body1"
        sx={{
          mb: 2,
          textAlign: 'left',
          fontSize: '15px',
          lineHeight: 1.5,
          maxWidth: '740px',
          color: 'text.secondary',
          fontStyle: 'italic'
        }}
      >
        {t('books:booksNote')}
      </Typography>

      <Typography 
        variant="caption" 
        sx={{ 
          display: 'block', 
          textAlign: 'right', 
          color: 'text.secondary',
          fontStyle: 'italic'
        }}
      >
        {t('shared:lastUpdated')}: 9/30/2025
      </Typography>
    </Box>
  );
}
